import { FC, useEffect } from "react";
import { View } from "react-native"
import { useGetIndicatorRiskQuery, useGetReportQuery } from "@/_store/company/company.slice";
import { ReportItem, useReportByType } from "@/_hooks/Report/useReportByType";
import { ReportTypeCompany } from "@/types/reportType";
import {
  POLLING_INTERVAL_MS,
  STATUS_COMPLETE,
  STATUS_IN_PROGRESS
} from "@/_utils/contstans";
import InfoAlertIcon from "@/assets/icons/main/blueAlert.svg";
import DangerAlertIcon from "@/assets/icons/main/redAlert.svg";
import HeaderReport from "@/app/components/HeaderReport/HeaderReport";
import useReportHandler from "@/_hooks/Report/useReportHandler";
import useShowToaster from "@/_hooks/Toasters/useShowToaster";
import InfoBlock, { InfoBlockType } from "@/app/components/InfoBlock";
import { getRiskLevel } from "@/_utils/getRiskLevel";
import SuccessLogo from "@/assets/icons/main/greenAlert.svg";
import { RiskLevelType } from "@/app/components/RiskIndicator/RiskIndicator";
import Container from "@/app/components/Container";
import { EdgeInsets, useSafeAreaInsets } from "react-native-safe-area-context";
import { useGetReportHistoryQuery } from "@/_store/currentUser/currentUser.slice";

interface ISuccessCompanyCheck {
  reportId?: string;
  updateReport?: any;
  isRefreshReport?: boolean;
}

const SuccessCompanyCheck: FC<ISuccessCompanyCheck> = ({
  reportId,
  updateReport,
  isRefreshReport = false
}) => {
  const {
    isPollingWhileGeneratingFile,
    IsShouldPollReportData,
    handleGenerate,
    notificationShown,
    setIsShouldPollReportData
  } = useReportHandler({ id: reportId!, isFiz: false });


  const { data: reportData} = useGetReportQuery(
    { id: reportId! },
    {
      skip: !reportId,
      pollingInterval: IsShouldPollReportData ? POLLING_INTERVAL_MS : 0,
    }
  );

  const { refetch: reportHistoryData } = useGetReportHistoryQuery(
    { page: 1, size: 4 },
    { skip: false }
  );

  useEffect(() => {
    if (reportData) {
      const timer = setTimeout(() => {
        reportHistoryData();
      }, 3500);

      return () => clearTimeout(timer);
    }
  }, [reportData]);

  const insets: EdgeInsets = useSafeAreaInsets();
  const reportByType: ReportItem = useReportByType(reportData?.responseData);
  const companyCard: ReportItem | undefined = reportByType[ReportTypeCompany.CompanyCard];
  const showNotification = useShowToaster();

  const companyInn: string = companyCard?.data?.basic?.inn;
  const personalInn: string = companyCard?.data?.basic?.person?.inn;
  const inn: string = companyInn || personalInn;

  const { data: riskData, isLoading: isRiskLoading } = useGetIndicatorRiskQuery(
    { inn: companyCard?.data?.basic?.inn || companyCard?.data?.basic?.person?.inn || "" },
    { skip: !inn }
  );

  const isProgress: boolean = companyCard?.status === STATUS_IN_PROGRESS;
  const isComplete: boolean = companyCard?.status === STATUS_COMPLETE;

  const {
    data: {
      FirmName,
      address,
      ceo = [],
      basic: {
        person: {
          fullName: personFullName,
          inn: personInn
        } = {},
        inn: basicInn,
        ogrn,
        ogrnip,
        region: {
          name: regionName
        } = {}
      } = {},
      message
    } = {}
  } = companyCard || {};

  const toStringValue = (value: any): string | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (typeof value === "object") {
      if (Array.isArray(value)) return value.length > 0 ? `Найдено записей: ${value.length}` : null;
      return null;
    }
    return null;
  };

  const COMPANY_DETAILS: InfoBlockType[] = [
    {
      label: "Наименование компании",
      value: toStringValue(FirmName) || toStringValue(personFullName) || null
    },
    {
      label: "Юридический адрес",
      value: toStringValue(address) || toStringValue(regionName) || null
    },
    {
      label: "Руководители",
      value: ceo?.length > 0 ?
        ceo.map((ceoObj: any) => ceoObj.person?.fullName).filter(Boolean).join(", ")
        : toStringValue(personFullName) || null
    },
    {
      label: "ИНН",
      value: toStringValue(basicInn) || toStringValue(personInn) || null
    },
    {
      label: "ОГРН",
      value: toStringValue(ogrn) || toStringValue(ogrnip) || null
    },
  ];

  const riskLevel: RiskLevelType | null = getRiskLevel([
    { when: isRiskLoading, level: "wait" },
    { when: IsShouldPollReportData, level: "wait" },
    { when: isPollingWhileGeneratingFile, level: "wait" },
    ...(riskData?.trust_worth ? [{ when: true, level: riskData.trust_worth }] : [])
  ]);

  const companyNameOrPerson: string = toStringValue(FirmName) || toStringValue(message) || toStringValue(personFullName) || "Информация отсутствует";

  useEffect(() => {
    if (IsShouldPollReportData || !companyCard?.data?.FirmName || notificationShown.current) return;

    showNotification({
      message: `Отчет сформирован ${companyNameOrPerson}`,
      icon: <SuccessLogo />,
    });

    notificationShown.current = true;
  }, [IsShouldPollReportData, companyCard]);

  useEffect(() => {
    if (isProgress) {
      setIsShouldPollReportData(true);

      showNotification({
        message: "Отчет формируется. Пожалуйста, подождите.",
        icon: <InfoAlertIcon />,
      });
    } else if (isComplete) {
      setIsShouldPollReportData(false);
    }
  }, [companyCard?.status]);

  const isGenerateFileOrLoadingData: boolean = IsShouldPollReportData || isPollingWhileGeneratingFile || isRefreshReport;

  const isShowDownloadButton: boolean =
    !IsShouldPollReportData &&
    Boolean(basicInn || personInn) &&
    !isPollingWhileGeneratingFile;


  const handleRefresh = async (): Promise<void> => {
    if (!reportId) return;

    try {
      await updateReport({ id: reportId, is_fiz: false }).unwrap();
    } catch (error: any) {
      const errorMessage: string = error?.data?.error || "При обновлении отчета произошла ошибка";

      showNotification({
        message: errorMessage,
        icon: <DangerAlertIcon />
      });
    }
  };

  return (
    <Container style={{ paddingBottom: insets.bottom + 60 }}>
      <HeaderReport
        reportData={reportData}
        riskLevel={riskLevel}
        companyNameOrPerson={companyNameOrPerson}
        isProgress={isGenerateFileOrLoadingData}
        isShowDownloadRefreshButtons={isShowDownloadButton}
        handleGenerate={handleGenerate}
        updateReport={handleRefresh}
      />
      <View>
        <InfoBlock
          isLoading={isGenerateFileOrLoadingData}
          rows={COMPANY_DETAILS}
          withBlock={true}
        />
      </View>
    </Container>
  );
};

export default SuccessCompanyCheck;
